import type { SxProps, Theme } from '@mui/material';
import type { Category } from '../types/Category';
import type { Content } from '../types/Content';

export const DOCUMENT_LAST_USED_STORAGE_KEY = 'narralizer-document-last-used';
export const CLOSED_DOCUMENTS_SORT_STORAGE_KEY = 'narralizer-closed-documents-sort';

export type ClosedDocumentsSortMode = 'name' | 'recent';

export function loadDocumentLastUsedMap(): Record<string, number> {
  try {
    const raw = localStorage.getItem(DOCUMENT_LAST_USED_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const result: Record<string, number> = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === 'number' && Number.isFinite(value)) {
        result[key] = value;
      }
    }
    return result;
  } catch {
    return {};
  }
}

export function recordDocumentUsed(documentId: string): number {
  const now = Date.now();
  const map = loadDocumentLastUsedMap();
  map[documentId] = now;
  try {
    localStorage.setItem(DOCUMENT_LAST_USED_STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore quota / private mode */
  }
  return now;
}

export function migrateDocumentLastUsed(oldId: string, newId: string): void {
  if (oldId === newId) return;
  const map = loadDocumentLastUsedMap();
  const ts = map[oldId];
  if (ts == null) return;
  map[newId] = Math.max(map[newId] ?? 0, ts);
  delete map[oldId];
  try {
    localStorage.setItem(DOCUMENT_LAST_USED_STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

export function removeDocumentLastUsed(documentId: string): void {
  const map = loadDocumentLastUsedMap();
  if (!(documentId in map)) return;
  delete map[documentId];
  try {
    localStorage.setItem(DOCUMENT_LAST_USED_STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

export function loadClosedDocumentsSort(): ClosedDocumentsSortMode {
  try {
    const v = localStorage.getItem(CLOSED_DOCUMENTS_SORT_STORAGE_KEY);
    if (v === 'recent' || v === 'lastChanged') {
      return 'recent';
    }
  } catch {
    /* ignore */
  }
  return 'name';
}

export function saveClosedDocumentsSort(mode: ClosedDocumentsSortMode): void {
  try {
    localStorage.setItem(CLOSED_DOCUMENTS_SORT_STORAGE_KEY, mode);
  } catch {
    /* ignore */
  }
}

export function compareDocumentsByName(a: { label: string }, b: { label: string }): number {
  return a.label.localeCompare(b.label, undefined, { numeric: true });
}

export function compareClosedDocuments(
  a: { id: string; label: string },
  b: { id: string; label: string },
  sort: ClosedDocumentsSortMode,
  lastUsedAt: Record<string, number>,
): number {
  if (sort === 'recent') {
    const diff = (lastUsedAt[b.id] ?? 0) - (lastUsedAt[a.id] ?? 0);
    if (diff !== 0) return diff;
  }
  return compareDocumentsByName(a, b);
}

export function extractDocumentName(buf: Uint8Array): string {
  let offset = 0;

  const readInt16 = (): number => {
    // Use little-endian byte order to match Java LITTLE_ENDIAN
    const value = buf[offset] | (buf[offset + 1] << 8);
    offset += 2;
    return value;
  };

  readInt16(); // skip buffer.getShort()
  offset += 1; // skip buffer.get()

  let size = buf[offset++] & 0xff;
  if (size === 0xff) {
    size = readInt16();
  }
  size = size * 2;

  const strBytes = new Uint8Array(size + 2);
  strBytes[0] = 0xff;
  strBytes[1] = 0xfe;
  strBytes.set(buf.slice(offset, offset + size), 2);
  offset += size;

  const decoder = new TextDecoder("utf-16le");
  const utf16Str = decoder.decode(strBytes);
  const utf8Bytes = new TextEncoder().encode(utf16Str);
  return new TextDecoder().decode(utf8Bytes).trim();
}

/** Content row ids on the clipboard during a cut (not copy). */
export function buildCutContentIdSet(
  isCutOperation: boolean,
  clipboardContents: Content[],
): ReadonlySet<string> {
  if (!isCutOperation || clipboardContents.length === 0) {
    return new Set();
  }
  return new Set(clipboardContents.map((c) => c.id));
}

/** Category ids on the clipboard during a cut (not copy). */
export function buildCutCategoryIdSet(
  isCutOperation: boolean,
  clipboardCategory: Category | null,
  clipboardCategories: Category[],
): ReadonlySet<string> {
  if (!isCutOperation) {
    return new Set();
  }
  const ids = new Set<string>();
  if (clipboardCategory) {
    ids.add(clipboardCategory.id);
  }
  for (const c of clipboardCategories) {
    ids.add(c.id);
  }
  return ids;
}

/** Muted styling for items cut to the clipboard but not yet pasted. */
export function cutPendingSx(isPending: boolean): SxProps<Theme> {
  if (!isPending) {
    return {};
  }
  return {
    opacity: 0.52,
    color: 'text.secondary',
    fontStyle: 'italic',
    '& .MuiSvgIcon-root': { color: 'text.disabled' },
  };
}

/** Values persisted as JVM LocalDateTime (UTC on the server) without a timezone in JSON. */
export type ServerDateTimeInput = string | number[] | null | undefined;

/** Parse server timestamps for display/comparison in the browser's local timezone. */
export function parseServerUtcDateTime(value: ServerDateTimeInput): Date | null {
  if (value == null) {
    return null;
  }
  if (Array.isArray(value)) {
    const [year, month, day, hour = 0, minute = 0, second = 0] = value;
    if (typeof year !== 'number' || typeof month !== 'number' || typeof day !== 'number') {
      return null;
    }
    return new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  }
  const trimmed = String(value).trim();
  if (!trimmed) {
    return null;
  }
  if (trimmed.includes('T') && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(trimmed)) {
    const date = new Date(`${trimmed}Z`);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(trimmed);
  return Number.isNaN(date.getTime()) ? null : date;
}

const DEFAULT_ADMIN_LOCAL_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
};

/** Format a server timestamp in the viewer's local timezone (admin console). */
export function formatAdminLocalDateTime(
  value: ServerDateTimeInput,
  options: Intl.DateTimeFormatOptions = DEFAULT_ADMIN_LOCAL_OPTIONS,
  fallback = '—',
): string {
  const date = parseServerUtcDateTime(value);
  if (!date) {
    return fallback;
  }
  return date.toLocaleString(undefined, options);
}
